/**
 * Shared-fields persistence helpers extracted from `useForm/index.tsx`.
 *
 * The "shared fields" feature persists a small subset of field values
 * (configured via `SHARED_FIELDS_CODENAME`) to `localStorage` so they
 * can be pre-filled when the user later sees a second-service form on
 * another domain in the same session.
 *
 * `saveSharedFields` writes the snapshot, `getSharedFields` reads it
 * asynchronously, and `fillFormWithSharedFields` dispatches the
 * snapshot back into the reducer.
 */

import { SHARED_FIELDS_CODENAME } from "@lib/shared/config";
import {
    ActionTypes,
    FieldData,
    FieldsData,
    FormReducerAction,
} from "../types";

export interface SharedFieldsMap {
    [x: string]: string;
}

interface UseSharedFieldsParams {
    dispatch: (action: FormReducerAction) => void;
}

export const useSharedFields = ({ dispatch }: UseSharedFieldsParams) => {
    const saveSharedFields = (newFieldsState: FieldsData) => {
        const SHARED_FIELDS: SharedFieldsMap = {};
        Object.keys(newFieldsState).forEach((fieldElement) => {
            if (SHARED_FIELDS_CODENAME.includes(fieldElement)) {
                SHARED_FIELDS[fieldElement] =
                    newFieldsState[fieldElement].value;
            }
        });
        localStorage.setItem("sharedFields", JSON.stringify(SHARED_FIELDS));
    };

    const fillFormWithSharedFields = (sharedFields: SharedFieldsMap) => {
        const newObj: { [x: string]: FieldData } = {};
        Object.keys(sharedFields).forEach((fieldElement) => {
            if (sharedFields[fieldElement]) {
                newObj[fieldElement] = {
                    valid: true,
                    errorMessage: "",
                    value: sharedFields[fieldElement],
                    changed: true,
                };
            }
        });
        dispatch({
            type: ActionTypes.SET_FIELDS_STATE,
            payload: { ...newObj },
        });
    };

    const getSharedFields = async (): Promise<SharedFieldsMap | undefined> => {
        try {
            const sharedFields = await new Promise((resolve, reject) => {
                const data = localStorage.getItem("sharedFields");
                if (data) {
                    try {
                        const parsedData = JSON.parse(data) as SharedFieldsMap;
                        resolve(parsedData);
                    } catch (error) {
                        reject(error);
                    }
                } else {
                    resolve(undefined);
                }
            });

            return sharedFields as Promise<SharedFieldsMap | undefined>;
        } catch (error) {
            return undefined;
        }
    };

    return {
        saveSharedFields,
        fillFormWithSharedFields,
        getSharedFields,
    };
};
